/**
 * Shared syndicate-join idempotency helpers (HATDEV-99283 / HATDEV-99287).
 * Used by the credit join CF call (`buycreditofferrequestv2`) and the post-purchase screen.
 */

const JOIN_IDEMPOTENCY_KEY_PREFIX = "hatdev_post_purchase_join_idempotency_";
const JOIN_COMPLETED_KEY_PREFIX = "hatdev_post_purchase_join_completed_";
const JOIN_RESPONSE_KEY_PREFIX = "hatdev_post_purchase_join_response_";
const PROCEED_CLAIMED_KEY_PREFIX = "hatdev_post_purchase_proceed_claimed_";

/**
 * Pending FE join-endpoint claims older than this are treated as stale and may be reclaimed
 * (Nadesh FE Must-Fix: ~60s TTL when explicit release was missed).
 */
export const JOIN_ENDPOINT_CLAIM_STALE_MS = 60_000;

/** Backend join_idempotency status as interpreted from buycreditofferrequestv2 responses. */
export type JoinBackendIdempotencyStatus = "completed" | "processing" | "failed" | "unknown";

/** In-memory dedupe when sessionStorage is missing or setItem fails. */
const joinMemoryClaims = new Set<string>();

/** Claim timestamps (ms) mirrored in memory when sessionStorage is unavailable. */
const joinEndpointClaimTimestamps = new Map<string, number>();

/**
 * Same-session unlock markers for join-endpoint claims.
 * Needed when release clears memory but sessionStorage.removeItem fails — tryClaim
 * ORs memory + session flag, so without this override a stale session flag would
 * keep blocking retries in the same page session.
 */
const joinEndpointClaimReleased = new Set<string>();

/** Coalesce concurrent CF calls that share the same idempotency key. */
const inFlightJoinRequests = new Map<string, unknown>();

export function getBrowserSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

export function readSessionFlag(storage: Storage | null | undefined, key: string): boolean {
  try {
    return storage?.getItem(key) === "1";
  } catch {
    return false;
  }
}

/** Returns true when the write succeeded. */
export function writeSessionFlag(storage: Storage | null | undefined, key: string): boolean {
  try {
    if (!storage) {
      return false;
    }
    storage.setItem(key, "1");
    return true;
  } catch {
    return false;
  }
}

export function readSessionValue(storage: Storage | null | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeSessionValue(
  storage: Storage | null | undefined,
  key: string,
  value: string
): boolean {
  try {
    if (!storage) {
      return false;
    }
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Stable idempotency key for the syndicate-join / purchase context.
 * Same purchase context always yields the same key (refresh-safe).
 * Backend join endpoints should treat repeated calls with this key as no-ops.
 */
export function buildJoinIdempotencyKey(contextKey: string): string {
  return `syndicate-join:${contextKey}`;
}

/**
 * Context key for the credit syndicate-join CF call (before a CP id exists).
 * Stable across retries / double-clicks for the same user + offer selection.
 */
export function buildCreditJoinContextKey(
  userId: string,
  offerDetails: {
    offerid?: string | number | null;
    offer_id?: string | number | null;
    draw_day?: string | string[] | null;
    line?: string | number | null;
    product_name?: string | null;
  } | null
    | undefined
): string {
  const offerId = offerDetails?.offerid ?? offerDetails?.offer_id ?? "unknown-offer";
  const drawDay = Array.isArray(offerDetails?.draw_day)
    ? offerDetails.draw_day.join(",")
    : offerDetails?.draw_day ?? "";
  const line = offerDetails?.line ?? "";
  const product = offerDetails?.product_name ?? "";
  return `credit:${userId}:${offerId}:${drawDay}:${line}:${product}`;
}

export function joinIdempotencySessionKey(contextKey: string): string {
  return `${JOIN_IDEMPOTENCY_KEY_PREFIX}${contextKey}`;
}

export function joinCompletedSessionKey(contextKey: string): string {
  return `${JOIN_COMPLETED_KEY_PREFIX}${contextKey}`;
}

export function joinResponseSessionKey(contextKey: string): string {
  return `${JOIN_RESPONSE_KEY_PREFIX}${contextKey}`;
}

export function proceedClaimedSessionKey(contextKey: string): string {
  return `${PROCEED_CLAIMED_KEY_PREFIX}${contextKey}`;
}

/** True only when the CF response body reports SUCCESS (not a top-level status alone). */
export function isCreditJoinResponseSuccess(
  result: { status?: string; response?: { status?: string } | null } | null | undefined
): boolean {
  return result?.response?.status === "SUCCESS";
}

/** Persist the real CF success payload so later replays do not fabricate a minimal SUCCESS. */
export function cacheJoinSuccessResponse(
  storage: Storage | null | undefined,
  contextKey: string,
  result: unknown
): void {
  try {
    writeSessionValue(storage, joinResponseSessionKey(contextKey), JSON.stringify(result));
  } catch {
    // Best-effort; completion flag still blocks a duplicate charge.
  }
}

/** Read a previously cached CF success payload, or null if missing/corrupt. */
export function readCachedJoinSuccessResponse<T = unknown>(
  storage: Storage | null | undefined,
  contextKey: string
): T | null {
  const raw = readSessionValue(storage, joinResponseSessionKey(contextKey));
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Resolve (and persist) the join idempotency key for this purchase context.
 * Does not mark the join completed — use markJoinCompletedForContext after success.
 */
export function ensureJoinIdempotencyKey(
  storage: Storage | null | undefined,
  contextKey: string
): string {
  const sessionKey = joinIdempotencySessionKey(contextKey);
  const existing = readSessionValue(storage, sessionKey);
  if (existing) {
    joinMemoryClaims.add(sessionKey);
    return existing;
  }
  const idempotencyKey = buildJoinIdempotencyKey(contextKey);
  joinMemoryClaims.add(sessionKey);
  writeSessionValue(storage, sessionKey, idempotencyKey);
  return idempotencyKey;
}

/** Persist that join/purchase for this context already succeeded (refresh-safe). */
export function markJoinCompletedForContext(
  storage: Storage | null | undefined,
  contextKey: string
): void {
  const sessionKey = joinCompletedSessionKey(contextKey);
  joinMemoryClaims.add(sessionKey);
  writeSessionFlag(storage, sessionKey);
  ensureJoinIdempotencyKey(storage, contextKey);
}

/** True when join/purchase for this context was already completed (incl. prior page load). */
export function isJoinCompletedForContext(
  storage: Storage | null | undefined,
  contextKey: string
): boolean {
  const sessionKey = joinCompletedSessionKey(contextKey);
  return joinMemoryClaims.has(sessionKey) || readSessionFlag(storage, sessionKey);
}

/** Session + memory key for the in-flight join-endpoint claim (not the durable idempotency key). */
export function joinEndpointClaimSessionKey(contextKey: string): string {
  return `${JOIN_IDEMPOTENCY_KEY_PREFIX}claim_${contextKey}`;
}

/**
 * Read claim timestamp (ms). Legacy `"1"` flags are treated as age-unknown (0 → immediately stale).
 * Returns null when no claim is present.
 */
export function readJoinEndpointClaimTimestamp(
  storage: Storage | null | undefined,
  contextKey: string
): number | null {
  const claimKey = joinEndpointClaimSessionKey(contextKey);
  const mem = joinEndpointClaimTimestamps.get(claimKey);
  if (typeof mem === "number" && Number.isFinite(mem)) {
    return mem;
  }
  const raw = readSessionValue(storage, claimKey);
  if (raw == null || raw === "") {
    return null;
  }
  if (raw === "1") {
    return 0;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function isJoinEndpointClaimStale(
  claimedAtMs: number,
  nowMs: number = Date.now(),
  staleMs: number = JOIN_ENDPOINT_CLAIM_STALE_MS
): boolean {
  if (!Number.isFinite(claimedAtMs) || claimedAtMs < 0) {
    return true;
  }
  return nowMs - claimedAtMs >= staleMs;
}

/**
 * True when a pending session claim survived a page refresh (no in-memory claim) and is not
 * yet stale — caller should probe backend idempotency status instead of hard-blocking.
 */
export function shouldProbeJoinIdempotencyBackendOnRefresh(
  storage: Storage | null | undefined,
  contextKey: string,
  nowMs: number = Date.now()
): boolean {
  if (isJoinCompletedForContext(storage, contextKey)) {
    return false;
  }
  const claimKey = joinEndpointClaimSessionKey(contextKey);
  if (joinMemoryClaims.has(claimKey)) {
    return false;
  }
  const claimedAt = readJoinEndpointClaimTimestamp(storage, contextKey);
  if (claimedAt == null) {
    return false;
  }
  return !isJoinEndpointClaimStale(claimedAt, nowMs);
}

/**
 * Map buycreditofferrequestv2 / join CF body (or error-shaped payload) to backend key status.
 */
export function interpretJoinBackendIdempotencyStatus(
  result: unknown
): JoinBackendIdempotencyStatus {
  if (isCreditJoinResponseSuccess(result as { response?: { status?: string } })) {
    return "completed";
  }
  const payload = (result && typeof result === "object" ? result : {}) as {
    status?: string;
    httpStatus?: number;
    code?: string;
    response?: { status?: string; httpStatus?: number; message?: string };
    details?: { httpStatus?: number; code?: string };
  };
  const status = String(payload.response?.status ?? payload.status ?? "").toUpperCase();
  const httpStatus =
    payload.response?.httpStatus ?? payload.httpStatus ?? payload.details?.httpStatus;
  const code = String(payload.code ?? payload.details?.code ?? "").toLowerCase();
  if (
    status === "IN_PROGRESS" ||
    httpStatus === 202 ||
    httpStatus === 409 ||
    code === "idempotency_in_progress"
  ) {
    return "processing";
  }
  if (
    status === "FAILED" ||
    status === "FAILURE" ||
    status === "ERROR" ||
    httpStatus === 422 ||
    code === "idempotency_payload_mismatch"
  ) {
    return "failed";
  }
  if (status) {
    return "failed";
  }
  return "unknown";
}

/**
 * Sync FE claim / completed flags from a backend idempotency status probe.
 * - completed → mark join completed + release pending claim
 * - failed → release pending claim so retry can reclaim
 * - processing / unknown → leave claim (60s stale TTL still applies)
 */
export function applyJoinBackendIdempotencyStatus(
  storage: Storage | null | undefined,
  contextKey: string,
  status: JoinBackendIdempotencyStatus
): void {
  if (status === "completed") {
    markJoinCompletedForContext(storage, contextKey);
    releaseJoinEndpointClaim(storage, contextKey);
    return;
  }
  if (status === "failed") {
    releaseJoinEndpointClaim(storage, contextKey);
  }
}

/**
 * Join-endpoint guard: first caller for a purchase context wins.
 * Subsequent calls (multi-click / refresh mid-flow) return claimed:false with the same key.
 * When the join is already completed, always returns claimed:false (safe no-op).
 * Pending claims older than {@link JOIN_ENDPOINT_CLAIM_STALE_MS} are treated as stale and reclaimed.
 */
export function tryClaimJoinEndpointGuard(
  storage: Storage | null | undefined,
  contextKey: string,
  nowMs: number = Date.now()
): { claimed: boolean; idempotencyKey: string; alreadyCompleted: boolean } {
  const idempotencyKey = ensureJoinIdempotencyKey(storage, contextKey);
  if (isJoinCompletedForContext(storage, contextKey)) {
    return { claimed: false, idempotencyKey, alreadyCompleted: true };
  }
  const claimKey = joinEndpointClaimSessionKey(contextKey);
  const releasedThisSession = joinEndpointClaimReleased.delete(claimKey);
  const claimedAt = readJoinEndpointClaimTimestamp(storage, contextKey);
  const hasPendingClaim =
    !releasedThisSession &&
    (joinMemoryClaims.has(claimKey) || claimedAt != null);

  if (hasPendingClaim) {
    const stale =
      claimedAt == null || isJoinEndpointClaimStale(claimedAt, nowMs);
    if (stale) {
      // Stale / legacy pending claim — unlock and fall through to reclaim.
      releaseJoinEndpointClaim(storage, contextKey);
      joinEndpointClaimReleased.delete(claimKey);
    } else {
      return { claimed: false, idempotencyKey, alreadyCompleted: false };
    }
  }

  joinMemoryClaims.add(claimKey);
  joinEndpointClaimTimestamps.set(claimKey, nowMs);
  writeSessionValue(storage, claimKey, String(nowMs));
  return { claimed: true, idempotencyKey, alreadyCompleted: false };
}

/**
 * Release an in-flight join-endpoint claim after FAILURE / TIMEOUT so a retry or refresh
 * can claim again. Does not clear join-completed, cached success, or the stable idempotency key.
 */
export function releaseJoinEndpointClaim(
  storage: Storage | null | undefined,
  contextKey: string
): void {
  const claimKey = joinEndpointClaimSessionKey(contextKey);
  joinMemoryClaims.delete(claimKey);
  joinEndpointClaimTimestamps.delete(claimKey);
  // Mark released before removeItem so same-session retry stays unlocked even if
  // storage removal throws (tryClaim ORs memory + session flag).
  joinEndpointClaimReleased.add(claimKey);
  try {
    storage?.removeItem(claimKey);
  } catch {
    // Best-effort. joinEndpointClaimReleased keeps same-session reclaim open;
    // after a full reload the stale TTL / backend probe can still unlock.
  }
}

/** Persist that Proceed / auto-nav already ran for this purchase context. */
export function markProceedClaimedForContext(
  storage: Storage | null | undefined,
  contextKey: string
): void {
  const sessionKey = proceedClaimedSessionKey(contextKey);
  joinMemoryClaims.add(sessionKey);
  writeSessionFlag(storage, sessionKey);
}

export function isProceedClaimedForContext(
  storage: Storage | null | undefined,
  contextKey: string
): boolean {
  const sessionKey = proceedClaimedSessionKey(contextKey);
  return joinMemoryClaims.has(sessionKey) || readSessionFlag(storage, sessionKey);
}

export function getInFlightJoinRequest<T>(idempotencyKey: string): T | undefined {
  return inFlightJoinRequests.get(idempotencyKey) as T | undefined;
}

export function setInFlightJoinRequest<T>(idempotencyKey: string, request: T): void {
  inFlightJoinRequests.set(idempotencyKey, request);
}

export function clearInFlightJoinRequest(idempotencyKey: string): void {
  inFlightJoinRequests.delete(idempotencyKey);
}

/** Test helper — clear in-memory join/proceed claims between specs. */
export function resetJoinIdempotencyMemoryForTests(): void {
  joinMemoryClaims.clear();
  joinEndpointClaimTimestamps.clear();
  joinEndpointClaimReleased.clear();
  inFlightJoinRequests.clear();
}
